import type { Variants } from "motion/react";

/**
 * The tally's one orchestrated moment: rows deal in, in sequence.
 *
 * The drama deliberately moved from *which prize* (a reel, which only has an answer once) to
 * *how much, all told* — so it scales with the win count instead of repeating per prize. The
 * deal is the only motion here; there is no per-row sparkle and no rays.
 */

/** The whole deal never runs longer than this, at any prize count. */
export const TALLY_DEAL_BUDGET_MS = 900;
/** Per-row gap at small counts, before the budget starts compressing it. */
export const TALLY_STAGGER_MAX_MS = 70;

/** Seconds between rows — capped so 3 rows feel deliberate and 20 still land inside the budget. */
export const tallyStagger = (rows: number): number =>
  Math.min(TALLY_STAGGER_MAX_MS, TALLY_DEAL_BUDGET_MS / Math.max(1, rows)) /
  1000;

/** `staggerChildren`, not per-row `delay` props: a re-render must not re-trigger the deal. */
export const tallyContainer = (rows: number): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: tallyStagger(rows) } },
});

/** One row's entrance. Kept under the house 500ms micro-interaction bar — the 900ms budget above
 *  is the envelope for the whole sequence, not for any single row. */
export const tallyRow: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 420, damping: 30, mass: 0.7 },
  },
};

/** Reduced motion: the stack is simply already there. */
export const tallyContainerStatic: Variants = { hidden: {}, visible: {} };
export const tallyRowStatic: Variants = {
  hidden: { opacity: 1 },
  visible: { opacity: 1 },
};
